// src/schema/covalue-schemas.ts
import { co, z } from "jazz-tools";
var AccountRef;
function setAccountReference(account) {
  AccountRef = account;
}
function getAccountReference() {
  return AccountRef;
}
var HierarchyNodeBaseData = z.object({
  /** Display name */
  name: z.string(),
  /** Node type discriminator - apps define their own */
  type: z.string(),
  /** Sharing mode */
  sharingMode: z.enum(["private", "shared", "public"]),
  /** Creation timestamp */
  createdAt: z.date(),
  /** Last update timestamp */
  updatedAt: z.date()
});
var HierarchyFolder = co.map({
  name: z.string(),
  type: z.literal("folder"),
  sharingMode: z.enum(["private", "shared", "public"]),
  createdAt: z.date(),
  updatedAt: z.date(),
  /** Whether folder is expanded in UI */
  expanded: z.boolean().optional(),
  /** Whether folder is archived (soft-deleted) */
  archived: z.boolean().optional(),
  /** When the folder was archived (for retention policies) */
  archivedAt: z.date().optional(),
  /** Parent folder reference (undefined for root-level) */
  get parent() {
    return co.optional(HierarchyFolder);
  },
  /**
   * Children collection - contains folders and items
   * Uses getter pattern for self-referential type
   */
  get children() {
    return co.optional(co.list(HierarchyFolder));
  }
});
var HierarchyItem = co.map({
  name: z.string(),
  type: z.literal("item"),
  sharingMode: z.enum(["private", "shared", "public"]),
  createdAt: z.date(),
  updatedAt: z.date(),
  /** Whether item is archived (soft-deleted) */
  archived: z.boolean().optional(),
  /** When the item was archived (for retention policies) */
  archivedAt: z.date().optional(),
  /** Parent folder reference (undefined for root-level) */
  get parent() {
    return co.optional(HierarchyFolder);
  }
});
var SharedReference = co.map({
  /** CoValue ID of the shared folder/item */
  targetId: z.string(),
  /** Path to the item (for display) */
  targetPath: z.string(),
  /** Display name */
  targetName: z.string(),
  /** Owner's account ID */
  ownerId: z.string(),
  /** Owner's display name */
  ownerName: z.string(),
  /** Permission level granted */
  role: z.enum(["admin", "writer", "reader"]),
  /** When share was created */
  sharedAt: z.date()
});
var HierarchyRoot = co.map({
  /** Creation timestamp */
  createdAt: z.date(),
  /** Top-level folders */
  folders: co.list(HierarchyFolder),
  /** Shared items from other users */
  sharedReferences: co.list(SharedReference).optional()
});
var ViewState = co.map({
  /** Which folders are expanded in the tree (folderId -> expanded) */
  folderExpanded: z.record(z.string(), z.boolean())
});
var UserSettings = co.map({
  // === Subscription fields ===
  /** Current subscription tier */
  subscriptionTier: z.optional(z.enum(["free", "plus", "premium", "enterprise"])),
  /** Subscription status */
  subscriptionStatus: z.optional(z.enum(["active", "past_due", "cancelled", "trialing", "beta"])),
  /** When the current subscription period ends (Unix timestamp) */
  subscriptionEndsAt: z.optional(z.number()),
  /** Cached limits for offline display */
  maxItems: z.optional(z.number()),
  /** Session/item retention days (for archiving policies) */
  retentionDays: z.optional(z.number()),
  /** Last time subscription was synced from backend */
  subscriptionSyncedAt: z.optional(z.number())
});

// src/schema/base-fields.ts
import { z as z2 } from "jazz-tools";
var hierarchyNodeBaseFields = {
  /** Display name */
  name: z2.string(),
  /** Sharing mode for permissions UI */
  sharingMode: z2.enum(["private", "shared", "public"]),
  /** Whether node is expanded in tree UI */
  expanded: z2.boolean().optional(),
  /** Soft-delete flag */
  archived: z2.boolean().optional(),
  /** When archived (for retention policies) */
  archivedAt: z2.date().optional(),
  /** Account ID of creator */
  createdBy: z2.string(),
  /** Creation timestamp */
  createdAt: z2.date(),
  /** Last update timestamp */
  updatedAt: z2.date()
};
var hierarchyRootBaseFields = {
  createdAt: z2.date()
};
var sharedReferenceBaseFields = {
  /** CoValue ID of the shared folder/item */
  targetId: z2.string(),
  /** Path to the item (for display) */
  targetPath: z2.string(),
  /** Display name */
  targetName: z2.string(),
  /** Owner's account ID */
  ownerId: z2.string(),
  /** Owner's display name */
  ownerName: z2.string(),
  /** Permission level granted */
  role: z2.enum(["admin", "writer", "reader"]),
  /** When share was created */
  sharedAt: z2.date()
};

// src/schema/factory.ts
import { co as co2, Group } from "jazz-tools";
function createNodeGroup(options) {
  const { account, parentNode, accountRole = "admin" } = options;
  const nodeGroup = Group.create({ owner: account });
  nodeGroup.addMember(account, accountRole);
  if (parentNode) {
    const parentGroup = parentNode.$jazz?.owner;
    if (parentGroup && typeof parentGroup.addMember === "function") {
      try {
        nodeGroup.addMember(parentGroup);
      } catch {
      }
    }
  }
  return nodeGroup;
}
function createFolder(name, owner, parent) {
  const now = /* @__PURE__ */ new Date();
  const childList = co2.list(HierarchyFolder).create([], owner);
  const folder = HierarchyFolder.create(
    {
      name,
      type: "folder",
      sharingMode: parent?.sharingMode ?? "private",
      expanded: false,
      archived: false,
      createdAt: now,
      updatedAt: now,
      parent,
      children: childList
    },
    owner
  );
  return folder;
}
function createItem(name, owner, parent) {
  const now = /* @__PURE__ */ new Date();
  const item = HierarchyItem.create(
    {
      name,
      type: "item",
      sharingMode: parent?.sharingMode ?? "private",
      archived: false,
      createdAt: now,
      updatedAt: now,
      parent
    },
    owner
  );
  return item;
}

// src/utils/node-utils.ts
function getNodeId(node) {
  if (node.$jazz?.id) return node.$jazz.id;
  if (node.id) return node.id;
  return `${node.type}:${node.name}`;
}
function isDescendantOf(node, potentialAncestor) {
  const ancestorId = potentialAncestor.$jazz?.id;
  if (!ancestorId) return false;
  let current = node.parent;
  while (current) {
    if (current.$jazz?.id === ancestorId) {
      return true;
    }
    current = current.parent;
  }
  return false;
}
function isSameOrDescendantOf(node, potentialAncestor) {
  const nodeId = node.$jazz?.id;
  const ancestorId = potentialAncestor.$jazz?.id;
  if (nodeId && ancestorId && nodeId === ancestorId) {
    return true;
  }
  return isDescendantOf(node, potentialAncestor);
}
function filterLoaded(list) {
  if (!list) return [];
  const result = [];
  for (const item of list) {
    if (item != null && item.$isLoaded !== false) {
      result.push(item);
    }
  }
  return result;
}
function filterDefinitelyLoaded(list) {
  if (!list) return [];
  const result = [];
  for (const item of list) {
    if (item != null && item.$isLoaded === true) {
      result.push(item);
    }
  }
  return result;
}
function getNodePathArray(node) {
  const parts = [];
  let current = node;
  while (current) {
    parts.unshift(current.name);
    current = current.parent;
  }
  return parts;
}
function getNodePathString(node) {
  return "/" + getNodePathArray(node).join("/");
}

// src/utils/type-guards.ts
function isLoaded(node) {
  return node != null && node.$isLoaded === true;
}
function maybeLoaded(node) {
  return node != null && node.$isLoaded !== false;
}
function isFolder(node) {
  return node.type === "folder";
}
function isItem(node) {
  return node.type === "item";
}
function isMap(node) {
  return node.type === "map";
}
function isArchived(node) {
  return node.archived === true;
}
function isActive(node) {
  return node.archived !== true;
}
function isCoValue(obj) {
  return obj != null && typeof obj === "object" && "$jazz" in obj && typeof obj.$jazz?.id === "string";
}
function getCoValueId(node) {
  if (isCoValue(node)) {
    return node.$jazz.id;
  }
  return void 0;
}
function getOwner(coValue) {
  if (coValue == null || typeof coValue !== "object") {
    throw new Error("Cannot get owner of null/undefined value");
  }
  const obj = coValue;
  const owner = obj.$jazz?.owner;
  if (!owner) {
    throw new Error("CoValue has no owner");
  }
  return owner;
}
function isIterable(value) {
  return value != null && typeof value === "object" && Symbol.iterator in value && typeof value[Symbol.iterator] === "function";
}
function toArray(value) {
  if (value == null) return [];
  if (Array.isArray(value)) return value;
  if (typeof value === "object" && Symbol.iterator in value) {
    return Array.from(value);
  }
  return [];
}
function iterateChildren(children) {
  if (!isIterable(children)) {
    return [];
  }
  const result = [];
  for (const child of children) {
    if (child != null) {
      result.push(child);
    }
  }
  return result;
}

// src/schema/utilities.ts
function getNodePath(node) {
  return getNodePathString(node);
}
function isDescendantOf2(node, potentialAncestor) {
  return isDescendantOf(node, potentialAncestor);
}
function findNodeByPath(folders, path) {
  const parts = path.split("/").filter(Boolean);
  if (parts.length === 0) return void 0;
  let currentList = folders;
  let currentNode;
  for (let i = 0; i < parts.length; i++) {
    const part = parts[i];
    currentNode = void 0;
    for (const child of currentList) {
      if (child && child.name === part) {
        currentNode = child;
        break;
      }
    }
    if (!currentNode) return void 0;
    if (i < parts.length - 1) {
      if (currentNode.type !== "folder") return void 0;
      const folder = currentNode;
      currentList = toArray(
        folder.children
      );
      if (currentList.length === 0) return void 0;
    }
  }
  return currentNode;
}

// src/errors.ts
var CircularReferenceError = class extends Error {
  constructor() {
    super("Cannot move folder into itself or its descendants");
    this.name = "CircularReferenceError";
  }
};
var NamingConflictError = class extends Error {
  /** The name that conflicts */
  conflictingName;
  constructor(name, type) {
    super(`A ${type} named "${name}" already exists at this location`);
    this.name = "NamingConflictError";
    this.conflictingName = name;
  }
};
var ItemLimitExceededError = class extends Error {
  /** Maximum items allowed for the user's tier (if known) */
  maxItems;
  constructor(maxItems) {
    const message = maxItems !== void 0 ? `You've reached your limit of ${maxItems} items. Upgrade to create more.` : "You've reached your item limit. Upgrade to create more.";
    super(message);
    this.name = "ItemLimitExceededError";
    this.maxItems = maxItems;
  }
};
var NodeNotLoadedError = class extends Error {
  /** The ID of the node that wasn't loaded */
  nodeId;
  constructor(nodeId) {
    const message = nodeId ? `Node ${nodeId} is not loaded` : "Required node is not loaded";
    super(message);
    this.name = "NodeNotLoadedError";
    this.nodeId = nodeId;
  }
};
var ArchiveOperationError = class extends Error {
  constructor(operation, reason) {
    const message = reason ? `Failed to ${operation} node: ${reason}` : `Failed to ${operation} node`;
    super(message);
    this.name = "ArchiveOperationError";
  }
};

// src/utils/id.ts
import { nanoid } from "nanoid";
function generateId(length = 10) {
  return nanoid(length);
}

// src/utils/path.ts
var DEFAULT_PATH_SEPARATOR = "";
function normalize(path, separator = DEFAULT_PATH_SEPARATOR) {
  return path.split(separator).filter(Boolean).join(separator);
}
function splitPath(path, separator = DEFAULT_PATH_SEPARATOR) {
  const normalized = normalize(path, separator);
  if (!normalized) return [];
  return normalized.split(separator);
}
function getParentPath(path, separator = DEFAULT_PATH_SEPARATOR) {
  const parts = splitPath(path, separator);
  if (parts.length <= 1) return "";
  return parts.slice(0, -1).join(separator);
}
function createChildPath(parentPath, childName, separator = DEFAULT_PATH_SEPARATOR) {
  const parent = normalize(parentPath, separator);
  if (!parent) return childName;
  return `${parent}${separator}${childName}`;
}
function isDescendantPath(descendant, ancestor, separator = DEFAULT_PATH_SEPARATOR) {
  const d = splitPath(descendant, separator);
  const a = splitPath(ancestor, separator);
  if (a.length === 0 || d.length <= a.length) return false;
  return a.every((part, i) => part === d[i]);
}

// src/utils/tree-utils.ts
function walkTree(folders, visitor) {
  for (const folder of folders) {
    if (!folder) continue;
    const result = visitor(folder);
    if (result === "stop") return true;
    if (result !== "skip" && folder.children) {
      const childArray = toArray(folder.children);
      if (childArray.length > 0) {
        const stopped = walkTree(childArray, visitor);
        if (stopped) return true;
      }
    }
  }
  return false;
}

// src/utils/account-guards.ts
function isAccountLoaded(account) {
  return typeof account === "object" && account !== null && "id" in account && "root" in account && typeof account.id === "string" && typeof account.root === "object" && account.root !== null;
}
function hasLoadedRoot(account) {
  return typeof account === "object" && account !== null && "root" in account && typeof account.root === "object" && account.root !== null && "folders" in account.root && typeof account.root.folders === "object" && account.root.folders !== null;
}
function hasLoadedField(root, field) {
  return field in root && root[field] !== void 0;
}

// src/operations.ts
function getContainingList(root, folder) {
  const parent = folder.parent;
  return parent?.children ?? root.folders;
}
function findFolderIndex(list, folder) {
  const folderId = folder.$jazz?.id;
  return [...list].findIndex((f) => {
    if (!f) return false;
    return f === folder || f.$jazz?.id === folderId;
  });
}
function getGroupMemberCount(group) {
  const members = group.members;
  if (Array.isArray(members)) {
    return members.length;
  }
  return 1;
}
function updateGroupMembership(node, currentParent, newParent) {
  const nodeGroup = node.$jazz?.owner;
  if (!nodeGroup) return;
  if (currentParent) {
    const oldParentGroup = currentParent.$jazz?.owner;
    if (oldParentGroup) {
      try {
        nodeGroup.removeMember(oldParentGroup);
      } catch {
      }
    }
  }
  if (newParent) {
    const newParentGroup = newParent.$jazz?.owner;
    if (newParentGroup) {
      try {
        nodeGroup.addMember(newParentGroup);
      } catch {
      }
      const memberCount = getGroupMemberCount(newParentGroup);
      if (memberCount > 1) {
        node.$jazz?.set("sharingMode", "shared");
      }
    }
  } else {
    const memberCount = getGroupMemberCount(nodeGroup);
    if (memberCount <= 1) {
      node.$jazz?.set("sharingMode", "private");
    }
  }
}
function findById(root, folderId) {
  if (!root?.folders || !folderId) return null;
  let found = null;
  walkTree(toArray(root.folders), (node) => {
    if (node.$jazz?.id === folderId) {
      found = node;
      return "stop";
    }
  });
  return found;
}
function getFolderPath(folder) {
  return getNodePathArray(folder);
}
function getFolderDisplayPath(folder) {
  return getFolderPath(folder).join("/");
}
function nameExists(root, name, parent) {
  const normalizedName = name.toLowerCase();
  const foldersToCheck = parent?.children ?? root?.folders ?? [];
  for (const folder of foldersToCheck) {
    if (folder && !folder.archived && folder.name?.toLowerCase() === normalizedName) {
      return true;
    }
  }
  return false;
}
function generateUniqueName(root, baseName, parent) {
  if (!nameExists(root, baseName, parent)) {
    return baseName;
  }
  for (let suffix = 2; suffix <= 100; suffix++) {
    const candidateName = `${baseName} (${suffix})`;
    if (!nameExists(root, candidateName, parent)) {
      return candidateName;
    }
  }
  return `${baseName} (${Date.now()})`;
}
function countFolders(root, predicate = () => true) {
  if (!root?.folders) return 0;
  let count = 0;
  walkTree(toArray(root.folders), (node) => {
    if (node.archived) return "skip";
    if (predicate(node)) count++;
  });
  return count;
}
function collectArchivedFolders(root) {
  if (!root?.folders) return [];
  const archived = [];
  walkTree(toArray(root.folders), (node) => {
    if (node.archived) {
      archived.push(node);
      return "skip";
    }
  });
  return archived;
}
function renameNode(node, name) {
  node.$jazz?.set("name", name);
  node.$jazz?.set("updatedAt", /* @__PURE__ */ new Date());
}
function deleteNode(root, node) {
  if (!root?.folders) return;
  if (node.children) {
    const childArr = node.children;
    const children = [...childArr].filter((c) => c != null);
    for (const child of children) {
      deleteNode(root, child);
    }
  }
  const list = getContainingList(root, node);
  const index = findFolderIndex(list, node);
  if (index !== -1) {
    list.$jazz.splice(index, 1);
  }
}
function moveNodeToIndex(root, node, newParent, index) {
  if (!root?.folders) return;
  if (newParent) {
    const nodeId = node.$jazz?.id;
    const parentId = newParent.$jazz?.id;
    if (nodeId && parentId && nodeId === parentId) {
      throw new CircularReferenceError();
    }
    if (isDescendantOf(newParent, node)) {
      throw new CircularReferenceError();
    }
  }
  const currentParent = node.parent;
  const currentParentId = currentParent?.$jazz?.id;
  const newParentId = newParent?.$jazz?.id;
  const isChangingParent = currentParentId !== newParentId;
  if (isChangingParent) {
    const destinationList = newParent?.children ?? root.folders;
    const conflictingNode = [...destinationList].find(
      (n) => n && !n.archived && n.name?.toLowerCase() === node.name?.toLowerCase() && n.$jazz?.id !== node.$jazz?.id
    );
    if (conflictingNode) {
      const nodeType = node.type ?? "item";
      throw new NamingConflictError(node.name, nodeType);
    }
  }
  if (isChangingParent) {
    const currentList = getContainingList(root, node);
    const oldIndex = findFolderIndex(currentList, node);
    if (oldIndex !== -1) {
      currentList.$jazz.splice(oldIndex, 1);
    }
    const targetList = newParent?.children ?? root.folders;
    targetList.$jazz.splice(index, 0, node);
    node.$jazz?.set("parent", newParent);
    updateGroupMembership(node, currentParent, newParent);
  } else {
    const parentList = getContainingList(root, node);
    const oldIndex = findFolderIndex(parentList, node);
    if (oldIndex !== -1) {
      parentList.$jazz.splice(oldIndex, 1);
      const adjustedIndex = oldIndex < index ? index - 1 : index;
      parentList.$jazz.splice(adjustedIndex, 0, node);
    }
  }
  node.$jazz?.set("updatedAt", /* @__PURE__ */ new Date());
}
function moveNode(root, node, newParent) {
  if (!root?.folders) return;
  const targetList = newParent?.children ?? root.folders;
  const targetLength = [...targetList].length;
  moveNodeToIndex(root, node, newParent, targetLength);
}
function reorderNode(root, node, newIndex) {
  const parent = node.parent;
  moveNodeToIndex(root, node, parent, newIndex);
}
function toggleExpanded(folder) {
  folder.$jazz?.set("expanded", !folder.expanded);
  folder.$jazz?.set("updatedAt", /* @__PURE__ */ new Date());
}
function setExpanded(folder, expanded) {
  folder.$jazz?.set("expanded", expanded);
  folder.$jazz?.set("updatedAt", /* @__PURE__ */ new Date());
}
function expandAncestors(folder) {
  let current = folder.parent;
  while (current) {
    current.$jazz?.set("expanded", true);
    current.$jazz?.set("updatedAt", /* @__PURE__ */ new Date());
    current = current.parent;
  }
}
function archiveNode(node) {
  const now = /* @__PURE__ */ new Date();
  node.$jazz?.set("archived", true);
  node.$jazz?.set("archivedAt", now);
  node.$jazz?.set("updatedAt", now);
  if (node.children) {
    const childArr = node.children;
    const children = [...childArr].filter((c) => c != null);
    for (const child of children) {
      if (!child.archived) {
        archiveNode(child);
      }
    }
  }
}
function unarchiveNode(node) {
  const now = /* @__PURE__ */ new Date();
  node.$jazz?.set("archived", false);
  node.$jazz?.set("archivedAt", void 0);
  node.$jazz?.set("updatedAt", now);
  if (node.children) {
    const childArr = node.children;
    const children = [...childArr].filter((c) => c != null);
    for (const child of children) {
      if (child.archived) {
        unarchiveNode(child);
      }
    }
  }
}
function emptyTrash(root) {
  if (!root?.folders) return 0;
  const archived = collectArchivedFolders(root);
  let deletedCount = 0;
  for (const folder of archived.reverse()) {
    try {
      deleteNode(root, folder);
      deletedCount++;
    } catch {
    }
  }
  return deletedCount;
}

// src/schema/archive.ts
function archiveFolder(folder) {
  archiveNode(folder);
}
function unarchiveFolder(folder) {
  unarchiveNode(folder);
}
function archiveItem(item) {
  archiveNode(item);
}
function unarchiveItem(item) {
  unarchiveNode(item);
}
function filterActiveNodes(nodes) {
  return nodes.filter((node) => node != null && !isArchived(node));
}
function filterArchivedNodes(nodes) {
  return nodes.filter((node) => node != null && isArchived(node));
}

// src/schema/migration.ts
function ensureBaseFields(node) {
  let migrated = false;
  const now = /* @__PURE__ */ new Date();
  if (node.archived === void 0) {
    node.$jazz.set("archived", false);
    migrated = true;
  }
  if (!node.sharingMode) {
    node.$jazz.set("sharingMode", "private");
    migrated = true;
  }
  if (!node.createdAt) {
    node.$jazz.set("createdAt", now);
    migrated = true;
  }
  if (!node.updatedAt) {
    node.$jazz.set("updatedAt", now);
    migrated = true;
  }
  return migrated;
}
function ensureFolderBaseFields(folder) {
  return ensureBaseFields(folder);
}
function ensureItemBaseFields(item) {
  return ensureBaseFields(item);
}
function migrateAllFolders(folders, _options) {
  let count = 0;
  for (const folder of folders) {
    if (!folder) continue;
    if (ensureBaseFields(folder)) {
      count++;
    }
    const childrenArray = toArray(
      folder.children
    );
    if (childrenArray.length > 0) {
      count += migrateAllFolders(childrenArray, _options);
    }
  }
  return count;
}

// src/constants.ts
var ARCHIVE_SETTINGS = {
  /** Timestamp format for archived item names */
  timestampFormat: "YYYY-MM-DD HH:mm",
  /** Regex pattern to match archived name suffix */
  nameSuffixPattern: / \(archived \d{4}-\d{2}-\d{2} \d{2}:\d{2}\)$/
};
function formatArchiveTimestamp(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${year}-${month}-${day} ${hours}:${minutes}`;
}
function getArchiveNameSuffix(date = /* @__PURE__ */ new Date()) {
  return ` (archived ${formatArchiveTimestamp(date)})`;
}
function removeArchiveSuffix(name) {
  return name.replace(ARCHIVE_SETTINGS.nameSuffixPattern, "");
}
var UI_DEFAULTS = {
  /** Default tree indentation per level (px) */
  treeIndent: 16,
  /** Default row height (px) */
  rowHeight: 32,
  /** Max visible actions in row buttons */
  maxRowActions: 2,
  /** Delay before drag starts (ms) for touch */
  touchDragDelay: 250,
  /** Movement threshold before drag starts (px) */
  dragThreshold: 8
};
export {
  ARCHIVE_SETTINGS,
  ArchiveOperationError,
  CircularReferenceError,
  DEFAULT_PATH_SEPARATOR,
  HierarchyFolder,
  HierarchyItem,
  HierarchyNodeBaseData,
  HierarchyRoot,
  ItemLimitExceededError,
  NamingConflictError,
  NodeNotLoadedError,
  SharedReference,
  UI_DEFAULTS,
  UserSettings,
  ViewState,
  archiveFolder,
  archiveItem,
  archiveNode,
  collectArchivedFolders,
  countFolders,
  createChildPath,
  createFolder,
  createItem,
  createNodeGroup,
  deleteNode,
  emptyTrash,
  ensureBaseFields,
  ensureFolderBaseFields,
  ensureItemBaseFields,
  expandAncestors,
  filterActiveNodes,
  filterArchivedNodes,
  filterDefinitelyLoaded,
  filterLoaded,
  findById,
  findNodeByPath,
  formatArchiveTimestamp,
  generateId,
  generateUniqueName,
  getAccountReference,
  getArchiveNameSuffix,
  getCoValueId,
  getFolderDisplayPath,
  getFolderPath,
  getNodeId,
  getNodePath,
  getNodePathArray,
  getNodePathString,
  getOwner,
  getParentPath,
  hasLoadedField,
  hasLoadedRoot,
  hierarchyNodeBaseFields,
  hierarchyRootBaseFields,
  isAccountLoaded,
  isActive,
  isArchived,
  isCoValue,
  isDescendantOf2 as isDescendantOf,
  isDescendantPath,
  isFolder,
  isItem,
  isIterable,
  isLoaded,
  isMap,
  isSameOrDescendantOf,
  iterateChildren,
  maybeLoaded,
  migrateAllFolders,
  moveNode,
  moveNodeToIndex,
  nameExists,
  removeArchiveSuffix,
  renameNode,
  reorderNode,
  setAccountReference,
  setExpanded,
  sharedReferenceBaseFields,
  splitPath,
  toArray,
  toggleExpanded,
  unarchiveFolder,
  unarchiveItem,
  unarchiveNode,
  walkTree
};
